import type { CapabilityItem, ClientDiff } from "./clients";
import { permissionConsequence, permissionPhrase } from "./permissionCopy";
export type { Agent } from "./agents";
export interface Proposal {
  id: string;
  organization_id: string;
  agent_id: string;
  operation_id: string;
  arguments: {
    path?: Record<string, string>;
    body?: Record<string, unknown>;
    diff?: {
      capabilities_added?: string[];
      capabilities_removed?: string[];
      redirect_uris_added?: string[];
      redirect_uris_removed?: string[];
      keys_changed?: boolean;
    };
    [key: string]: unknown;
  };
  arguments_hash: string;
  mode: "suggest" | "draft";
  justification: string;
  risk: "low" | "medium" | "high";
  state:
    | "pending"
    | "accepted"
    | "rejected"
    | "executed"
    | "failed"
    | "stale"
    | "expired"
    | "cancelled";
  based_on: Record<string, string>;
  expires_at: string;
  created_at: string;
  result?: Record<string, unknown>;
  evidence?: { kind: string; ref: string; excerpt?: string }[];
  decision?: {
    by: string;
    at: string;
    outcome: "accept" | "edit" | "reject";
    reason?: string;
  };
}
type Reviewable = Pick<Proposal, "operation_id" | "arguments">;
export const proposalsPath = (org: string) =>
  `/v1/organizations/${encodeURIComponent(org)}/proposals`;
export const proposalPath = (org: string, proposal: string) =>
  `${proposalsPath(org)}/${encodeURIComponent(proposal)}`;
export const proposalHref = (org: string, proposal: string) =>
  `/approvals/${encodeURIComponent(org)}/${encodeURIComponent(proposal)}`;
export function proposalExpiry(expiresAt: string, now = new Date()): string {
  const expiry = new Date(expiresAt);
  const calendarDay = (date: Date) =>
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((calendarDay(expiry) - calendarDay(now)) / 86400000);
  const day =
    days === 0
      ? "today"
      : days === 1
        ? "tomorrow"
        : new Intl.DateTimeFormat("en", {
            weekday: "short",
            month: "short",
            day: "numeric",
          }).format(expiry);
  const time = new Intl.DateTimeFormat("en", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(expiry);
  return `expires ${day} at ${time}`;
}
export function proposalDiff(proposal: Reviewable): ClientDiff {
  const diff = proposal.arguments.diff;
  return {
    added: diff?.capabilities_added ?? [],
    removed: diff?.capabilities_removed ?? [],
    redirectsAdded: diff?.redirect_uris_added ?? [],
    redirectsRemoved: diff?.redirect_uris_removed ?? [],
    keysChanged: diff?.keys_changed ?? false,
    nameChanged: typeof proposal.arguments.body?.client_name === "string",
  };
}
export function proposalSentence(
  proposal: Reviewable,
  catalogue: CapabilityItem[],
  appName = "This app",
): string {
  if (proposal.operation_id === "updateClient")
    return permissionConsequence(appName, proposalDiff(proposal), catalogue);
  if (proposal.operation_id === "createClient")
    return "An agent wants to register an app";
  if (proposal.operation_id === "revokeClient")
    return `${appName} will lose access to your organisation`;
  const permission = catalogue.find((permission) =>
    permission.operations.includes(proposal.operation_id),
  );
  return permission
    ? `An agent wants to ${permissionPhrase([permission.capability], catalogue)}`
    : "An agent wants to make a change that needs your review";
}
/** Access changes always verify, including keys and redirects. Unclassified operations fail closed. */
export function proposalNeedsVerification(
  proposal: Reviewable,
  catalogue: CapabilityItem[],
): boolean {
  if (proposal.operation_id === "updateClient") {
    const diff = proposalDiff(proposal);
    if (
      !proposal.arguments.diff ||
      diff.keysChanged ||
      diff.added.length ||
      diff.redirectsAdded.length
    )
      return true;
    return diff.removed.some((capability) => {
      const risk = catalogue.find(
        (item) => item.capability === capability,
      )?.risk;
      return !risk || risk === "money";
    });
  }
  const guarded = catalogue.filter((permission) =>
    permission.operations.includes(proposal.operation_id),
  );
  return (
    !guarded.length ||
    guarded.some(
      (permission) =>
        permission.risk === "access" || permission.risk === "money",
    )
  );
}
